import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, MagnifyingGlass, X, Wrench, ArrowClockwise, WarningCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * EquipmentPicker · Selector unificado que consume el catálogo de mantenimiento
 * (colección `maint_equipment`) para reemplazar cualquier input libre de "Equipo"
 * en la plataforma.
 *
 * Props:
 *   value        Valor actual. Depende del modo:
 *                  - mode="id"    → id (uuid) del equipo
 *                  - mode="code"  → código (ej. "JMB-04")
 *                  - mode="label" → texto "CODE · Name" (para compat con InspectionsTab)
 *   onChange     Callback (newValue, fullEquipmentObject|null)
 *   mode         "id" | "code" | "label"        default: "id"
 *   className    Clases extra
 *   placeholder  Placeholder
 *   allowClear   Muestra botón "x" (default true)
 *   dataTestId
 *   filterFn     Filtro adicional para excluir equipos (ej. deshabilitados)
 *   compact      Reduce padding
 *   allowInlineAdd  Iter132 · muestra botón "+ agregar al catálogo" cuando
 *                   no hay match en la búsqueda. Requiere permiso operations.write.
 *   defaultType     Iter132 · pre-selecciona el tipo (ej. "jumbo", "scoop") al
 *                   crear un equipo inline desde bitácora.
 */
// Iter108: caché tri-estado. `list=null` significa "no cargado".
// `list=[]` significa "cargado pero vacío" (respuesta 200 sin equipos).
// `error=true` significa "última carga falló" → NO cacheamos [] y permitimos
// retry cuando el operador reabre el dropdown o toca "Recargar".
const CACHE = { list: null, promise: null, error: false };
async function _loadEquipment(force = false) {
  if (force) { CACHE.list = null; CACHE.promise = null; CACHE.error = false; }
  if (CACHE.list !== null && !CACHE.error) return CACHE.list;
  if (CACHE.promise) return CACHE.promise;
  // Iter108: bust del cache del Service Worker con timestamp para evitar
  // respuestas 403 stale de versiones antiguas (Iter100 fix).
  const bust = Date.now();
  CACHE.promise = api.get(`/maintenance/equipment/lite-public?_t=${bust}`)
    .then((r) => {
      CACHE.list = Array.isArray(r.data) ? r.data : [];
      CACHE.error = false;
      CACHE.promise = null;
      return CACHE.list;
    })
    .catch((err) => {
      // Iter108: NO envenenar la caché con [] en caso de error — así el
      // próximo montaje del picker reintenta automáticamente.
      CACHE.error = true;
      CACHE.promise = null;
      console.warn("[EquipmentPicker] error cargando catálogo:", err?.message || err);
      return [];   // valor temporal para no romper el render actual
    });
  return CACHE.promise;
}

/** Invalida la caché — llamable desde afuera si un equipo nuevo se creó. */
export function reloadEquipmentCache() {
  CACHE.list = null;
  CACHE.promise = null;
  CACHE.error = false;
}

export default function EquipmentPicker({
  value = "",
  onChange,
  mode = "id",
  className = "",
  placeholder = "Selecciona equipo…",
  allowClear = true,
  dataTestId = "equipment-picker",
  filterFn,
  compact = false,
  allowInlineAdd = false,
  defaultType = "",
}) {
  const [equipments, setEquipments] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loadState, setLoadState] = useState("idle");   // idle | loading | ok | error
  const [inlineOpen, setInlineOpen] = useState(false);
  const [inlineCode, setInlineCode] = useState("");
  const [inlineName, setInlineName] = useState("");
  const [inlineSaving, setInlineSaving] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);

  const doLoad = useCallback((force = false) => {
    setLoadState("loading");
    _loadEquipment(force).then((list) => {
      setEquipments(filterFn ? (list || []).filter(filterFn) : (list || []));
      setLoadState(CACHE.error ? "error" : "ok");
    });
  }, [filterFn]);

  useEffect(() => {
    let alive = true;
    setLoadState("loading");
    _loadEquipment().then((list) => {
      if (!alive) return;
      setEquipments(filterFn ? (list || []).filter(filterFn) : (list || []));
      setLoadState(CACHE.error ? "error" : "ok");
    });
    return () => { alive = false; };
  }, [filterFn]);

  useEffect(() => {
    const outside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);

  // Buscar el equipo actualmente seleccionado por el valor
  const selected = useMemo(() => {
    if (!value) return null;
    return equipments.find((eq) => {
      if (mode === "id")    return eq.id === value;
      if (mode === "code")  return (eq.code || "").toUpperCase() === String(value).toUpperCase();
      /* label */
      return `${eq.code} · ${eq.name}` === value || eq.code === value || eq.name === value;
    }) || null;
  }, [equipments, value, mode]);

  // Filtrado por búsqueda
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return equipments.slice(0, 60);
    return equipments
      .filter((eq) => (`${eq.code} ${eq.name} ${eq.type || ""} ${eq.criticality || ""}`).toLowerCase().includes(s))
      .slice(0, 60);
  }, [equipments, q]);

  const emitChange = useCallback((eq) => {
    if (!eq) { onChange?.("", null); return; }
    let out = eq.id;
    if (mode === "code")  out = eq.code || "";
    if (mode === "label") out = `${eq.code} · ${eq.name}`;
    onChange?.(out, eq);
  }, [mode, onChange]);

  // Iter132 · alta inline
  const openInline = () => {
    const prefill = (q || "").trim().toUpperCase();
    setInlineCode(prefill);
    setInlineName("");
    setInlineOpen(true);
  };
  const saveInline = async () => {
    const code = (inlineCode || "").trim().toUpperCase();
    const name = (inlineName || "").trim();
    if (!code || !name) return;
    setInlineSaving(true);
    try {
      const r = await api.post("/maintenance/equipment/adhoc", {
        code, name, type: defaultType || "", criticality: "C",
      });
      // Invalidar caché para incorporar el equipo nuevo
      reloadEquipmentCache();
      const newEq = r.data;
      setEquipments((prev) => [...prev, newEq]);
      emitChange(newEq);
      setInlineOpen(false);
      setOpen(false);
      setQ("");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Error";
      // eslint-disable-next-line no-alert
      alert(`No se pudo agregar el equipo: ${msg}`);
    } finally {
      setInlineSaving(false);
    }
  };

  const label = selected
    ? `${selected.code || ""}${selected.name ? ` · ${selected.name}` : ""}`
    : (mode !== "id" && value ? value : "");

  return (
    <div ref={ref} className={`relative ${className}`} data-testid={dataTestId}>
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          // Iter108: si el picker abre y no hay equipos cargados, reintenta
          // silenciosamente — resuelve el caso "operador con SW cache stale".
          if (loadState === "error" || (loadState === "ok" && equipments.length === 0)) {
            doLoad(true);
          }
          setTimeout(() => inputRef.current?.focus(), 30);
        }}
        className={`w-full flex items-center justify-between gap-2 border border-slate-200 rounded-md bg-white text-left ${compact ? "px-2 py-1 h-8 text-xs" : "px-3 py-2 h-9 text-sm"} hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-900/30`}
        data-testid={`${dataTestId}-btn`}
      >
        <span className={`truncate ${selected ? "text-slate-800 font-medium" : "text-slate-400"}`}>
          {label || placeholder}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {loadState === "error" && (
            <WarningCircle size={12} weight="fill" className="text-amber-500" title="Error cargando catálogo — click para reintentar" />
          )}
          {allowClear && selected && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); emitChange(null); }}
              className="text-slate-400 hover:text-red-600 cursor-pointer"
              title="Quitar selección"
            >
              <X size={12} weight="bold" />
            </span>
          )}
          <CaretDown size={12} weight="bold" className="text-slate-400" />
        </div>
      </button>

      {open && (
        <div
          className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col"
          data-testid={`${dataTestId}-dropdown`}
        >
          <div className="p-2 border-b border-slate-100 bg-slate-50 relative flex items-center gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={12} className="absolute left-2 top-2 text-slate-400" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por código, nombre, tipo…"
                className="w-full h-7 pl-6 pr-2 text-xs border border-slate-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-900/30"
                data-testid={`${dataTestId}-search`}
              />
            </div>
            <button
              type="button"
              onClick={() => doLoad(true)}
              className="h-7 px-1.5 text-slate-500 hover:text-blue-800 rounded border border-slate-200 bg-white"
              title="Recargar catálogo"
              data-testid={`${dataTestId}-reload`}
            >
              <ArrowClockwise size={12} weight="bold" className={loadState === "loading" ? "animate-spin" : ""} />
            </button>
          </div>
          <div className="overflow-y-auto flex-1">
            {loadState === "loading" && filtered.length === 0 ? (
              <p className="text-xs text-slate-400 italic px-3 py-4 text-center">
                Cargando catálogo…
              </p>
            ) : loadState === "error" ? (
              <div className="px-3 py-4 text-center">
                <p className="text-xs text-amber-700 font-medium mb-1">
                  ⚠ Error cargando el catálogo
                </p>
                <p className="text-[10px] text-slate-500 mb-2">
                  Revisa tu conexión y toca "Recargar" arriba a la derecha.
                </p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-4 text-center">
                <p className="text-xs text-slate-400 italic mb-2">
                  Sin equipos que coincidan.
                </p>
                {allowInlineAdd && (
                  <button
                    type="button"
                    onClick={openInline}
                    className="text-[11px] font-semibold text-blue-800 hover:text-blue-900 underline underline-offset-2"
                    data-testid={`${dataTestId}-inline-add`}
                  >
                    + Agregar {q ? `"${q.trim().toUpperCase()}"` : "un equipo"} al catálogo
                  </button>
                )}
                {!allowInlineAdd && (
                  <p className="text-[10px] text-slate-400">
                    Revisa el catálogo en Mantenimiento → Equipos.
                  </p>
                )}
              </div>
            ) : (
              filtered.map((eq) => (
                <button
                  key={eq.id}
                  type="button"
                  onClick={() => { emitChange(eq); setOpen(false); setQ(""); }}
                  className={`w-full text-left px-3 py-1.5 hover:bg-blue-50 text-xs border-b border-slate-50 last:border-0 flex items-center gap-2 ${
                    selected?.id === eq.id ? "bg-blue-100" : ""
                  }`}
                  data-testid={`${dataTestId}-opt-${eq.code}`}
                >
                  <Wrench size={11} weight="duotone" className="text-blue-800 shrink-0" />
                  <span className="font-mono font-bold text-blue-900">{eq.code}</span>
                  <span className="text-slate-700 truncate flex-1">{eq.name}</span>
                  {eq.criticality && (
                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 shrink-0">
                      {eq.criticality}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
          {allowInlineAdd && filtered.length > 0 && (
            <div className="border-t border-slate-100 px-3 py-1.5 bg-slate-50">
              <button type="button" onClick={openInline}
                       className="text-[11px] font-semibold text-blue-800 hover:text-blue-900"
                       data-testid={`${dataTestId}-inline-add-footer`}>
                + Agregar {q ? `"${q.trim().toUpperCase()}"` : "otro"} al catálogo
              </button>
            </div>
          )}
        </div>
      )}

      {/* Iter132 · Diálogo alta rápida */}
      {inlineOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
              onClick={(e) => { if (e.target === e.currentTarget) setInlineOpen(false); }}
              data-testid={`${dataTestId}-inline-dialog`}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-[92vw] p-5 space-y-3">
            <h3 className="font-heading font-black text-slate-900 flex items-center gap-2">
              <Wrench size={16} weight="duotone" className="text-blue-800" />
              Agregar equipo al catálogo
            </h3>
            <p className="text-xs text-slate-500">
              Alta rápida desde bitácora. El responsable de Mantenimiento
              completará más tarde marca, horas y componentes.
            </p>
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-600">Código / Nº económico</label>
              <input value={inlineCode}
                      onChange={(e) => setInlineCode(e.target.value.toUpperCase())}
                      placeholder="Ej. JMB-04"
                      className="w-full h-9 px-2 border border-slate-300 rounded font-mono text-sm"
                      data-testid={`${dataTestId}-inline-code`} autoFocus />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-600">Nombre / descripción</label>
              <input value={inlineName}
                      onChange={(e) => setInlineName(e.target.value)}
                      placeholder="Ej. Jumbo Sandvik DD421"
                      className="w-full h-9 px-2 border border-slate-300 rounded text-sm"
                      data-testid={`${dataTestId}-inline-name`} />
            </div>
            {defaultType && (
              <p className="text-[10px] text-slate-500">
                Tipo: <span className="font-mono font-bold uppercase">{defaultType}</span>
                {" · "}Criticidad: <span className="font-bold">C</span> (editable después)
              </p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setInlineOpen(false)}
                       className="h-9 px-3 text-sm border border-slate-300 rounded hover:bg-slate-50">
                Cancelar
              </button>
              <button type="button" onClick={saveInline}
                       disabled={inlineSaving || !inlineCode || !inlineName}
                       className="h-9 px-4 text-sm bg-blue-900 hover:bg-blue-800 text-white rounded font-semibold disabled:opacity-50"
                       data-testid={`${dataTestId}-inline-save`}>
                {inlineSaving ? "Guardando…" : "Agregar al catálogo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
